/* eslint-disable consistent-return */
import {
  ElementType,
  KeyboardEvent,
  MutableRefObject,
  useEffect,
  useRef,
} from "react";

import { Nullable } from "web/types/Utils";

import getFocusableElements from "./getFocusableElements";

interface IUseModalFocusArgs {
  component: Nullable<ElementType>;
  modalRef: MutableRefObject<Nullable<HTMLDivElement>>;
  closeAction?: () => void;
  persistent?: Nullable<boolean>;
}

const useModalFocus = ({
  component: Component,
  modalRef,
  closeAction,
  persistent = false,
}: IUseModalFocusArgs) => {
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!Component) return;

    previouslyFocusedElementRef.current =
      (document.activeElement as HTMLElement | null) ?? null;

    const focusTimer = window.setTimeout(() => {
      const content = modalRef?.current;
      const root = modalRef?.current;

      const closeButton = content?.querySelector<HTMLElement>(
        '[data-t1="close_modal_button"]'
      );
      const focusables = getFocusableElements(content);

      if (closeButton && focusables.includes(closeButton)) {
        closeButton.focus();
      } else if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        root?.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      previouslyFocusedElementRef.current?.focus?.();
    };
  }, [Component]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" || event.key === "Esc") {
      if (!persistent && typeof closeAction === "function") {
        event.preventDefault();
        closeAction();
      }
      return;
    }

    if (event.key !== "Tab") return;

    const root = modalRef?.current;
    const content = modalRef?.current;
    const focusables = getFocusableElements(content);

    if (focusables?.length === 0) {
      event.preventDefault();
      root?.focus();
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    const first = focusables?.[0];
    const last = focusables?.[focusables.length - 1];

    if (event.shiftKey) {
      if (!active || active === first || !content?.contains(active)) {
        event.preventDefault();
        last?.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return { handleKeyDown };
};

export default useModalFocus;
